// PRD 08 §2.2 #8 — Disease Code table with inline Edit/Delete actions and a
// clickable status badge. Row click opens the back-reference panel; the status
// toggle and action buttons each stopPropagation so they don't also select.
// PRD §5 — inactive rows are dimmed.
// PRD §7 (Req Clarification Q3) — Edit/Delete + status toggle require METADATA:WRITE. READ users
// see the management column with the actions VISIBLE but disabled, not hidden; PRD 21 §7.4 P-2
// supplies the wording.

import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';

import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

import type { DiseaseCode } from '../types/disease-code.types';
import { DiseaseCodeStatusToggle } from './DiseaseCodeStatusToggle';

interface DiseaseCodeTableProps {
  codes: DiseaseCode[];
  selectedCodeId: number | null;
  onRowSelect: (id: number) => void;
  onEdit: (code: DiseaseCode) => void;
  onDelete: (code: DiseaseCode) => void;
  canWrite: boolean;
}

export function DiseaseCodeTable({
  codes,
  selectedCodeId,
  onRowSelect,
  onEdit,
  onDelete,
  canWrite,
}: DiseaseCodeTableProps) {
  const { t } = useTranslation('admin');
  // SHCT-582 (QC re-test) — these were `aria-disabled` only, so an ordinary click still reached the
  // handler and answered with P-3's toast. §7.4 P-2 is 후속 동작 없음, and only a real `disabled`
  // brings the control within reach of the L2 guard.
  const { denyProps } = useWriteDenyGuard(canWrite);

  return (
    <>
    <table className="table">
      <thead>
        <tr>
          <th style={{ width: 110 }}>{t('diseaseCodeManagement.codePanel.columns.code')}</th>
          <th style={{ width: 220 }}>{t('diseaseCodeManagement.codePanel.columns.name')}</th>
          <th>{t('diseaseCodeManagement.codePanel.columns.description')}</th>
          <th style={{ width: 90 }}>{t('diseaseCodeManagement.codePanel.columns.status')}</th>
          <th style={{ width: 160 }}>
            {t('diseaseCodeManagement.codePanel.columns.manage')}
          </th>
        </tr>
      </thead>
      <tbody>
        {codes.map((c) => {
          const isSelected = c.id === selectedCodeId;
          const rowStyle: React.CSSProperties = {
            cursor: 'pointer',
            opacity: c.status === 'INACTIVE' ? 0.55 : 1,
            background: isSelected ? 'var(--badge-primary-bg)' : undefined,
          };
          return (
            <tr key={c.id} style={rowStyle} onClick={() => onRowSelect(c.id)}>
              <td>
                <span style={{ fontWeight: 600, color: 'var(--info)' }}>{c.code}</span>
              </td>
              <td>{c.name}</td>
              <td>
                <span
                  title={c.description ?? ''}
                  style={{
                    display: 'inline-block',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--muted-foreground)',
                    maxWidth: 380,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    verticalAlign: 'middle',
                  }}
                >
                  {c.description ?? ''}
                </span>
              </td>
              <td>
                <DiseaseCodeStatusToggle code={c} canWrite={canWrite} />
              </td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    disabled={!canWrite}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(c);
                    }}
                    {...denyProps}
                  >
                    <Pencil size={12} />
                    {t('diseaseCodeManagement.codePanel.edit')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    disabled={!canWrite}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(c);
                    }}
                    {...denyProps}
                  >
                    <Trash2 size={12} />
                    {t('diseaseCodeManagement.codePanel.delete')}
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </>
  );
}
